import { Button, Checkbox, Form, Modal, Tooltip } from "antd";
import { isEqual, sortBy } from "lodash-es";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ModalDivider } from "~components/ModalDivider/ModalDivider.tsx";
import { formStyles } from "~pages/Fixtures/FixtureEditPage/FixtureEditPage.styles.ts";
import type { TableColumnGroupDefinition } from "~types/tableColumnGroupDefinition";
import { getTableColumns } from "~utilities/getTableColumns";
import { saveToLocalStorage } from "~utilities/saveToLocalStorage.ts";
import { sortStringValues } from "~utils/utilities.ts";

type Props = {
  columns: Array<{ key: any; title: any }>;
  tableConfiguration: Array<TableColumnGroupDefinition>;
  localStorageKey: string;
  setSelectedColumns: (fields: any) => void;
  modalWidth?: number;
};

export const TableColumnSettings = ({
  columns,
  tableConfiguration,
  localStorageKey,
  setSelectedColumns,
  modalWidth,
}: Props) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isSelectAllChecked, setSelectAllChecked] = useState(false);
  const { formatMessage } = useIntl();

  const handleOk = async () => {
    const { fields } = await form.validateFields();
    saveToLocalStorage(localStorageKey, JSON.stringify(fields));
    setSelectedColumns(fields);
    setOpen(false);
  };

  const allColumns = tableConfiguration.flatMap((x) => x.columns);

  const defaultColumns = allColumns.filter((x) => x.isDefault);

  const allColumnsStringifies = allColumns.map((x) => JSON.stringify(x.key));

  const defaultColumnsStringifies = defaultColumns.map((x) => JSON.stringify(x.key));

  return (
    <>
      <Modal
        width={modalWidth || 800}
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        afterOpenChange={(visible) => {
          if (visible) {
            form.setFieldsValue({ fields: getTableColumns(localStorageKey, tableConfiguration) });
          }
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: "flex", gap: 5, justifyContent: "end" }}>
            <Button
              onClick={() => {
                setSelectAllChecked(false);
                form.setFieldsValue({
                  fields: defaultColumnsStringifies,
                });
              }}
              style={{ marginRight: "auto" }}
            >
              {formatMessage({ id: "default" })}
            </Button>
            <CancelBtn />
            <OkBtn />
          </div>
        )}
        onOk={handleOk}
        okText={formatMessage({ id: "ok" })}
      >
        <h5>
          <FormattedMessage id="table.settings.columns.modal.title" />
        </h5>
        <Form
          form={form}
          style={formStyles}
          onValuesChange={() => {
            const data = form.getFieldsValue(true);
            const formFieldsValues: Array<string> = data?.fields || [];
            const allFieldsSelected = isEqual(sortBy(formFieldsValues), sortBy(allColumnsStringifies));

            setSelectAllChecked(allFieldsSelected);
          }}
        >
          <Form.Item style={{ marginBottom: 0 }}>
            <Checkbox
              checked={isSelectAllChecked}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setSelectAllChecked(isChecked);
                const values = isChecked ? allColumnsStringifies : [];
                form.setFieldsValue({ fields: values });
              }}
            >
              <FormattedMessage id="select.all" />
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="fields"
            rules={[{ required: true, message: formatMessage({ id: "table.settings.columns.error" }) }]}
          >
            <Checkbox.Group style={{ width: "100%" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", width: "100%" }}>
                {tableConfiguration
                  .sort((a, b) =>
                    sortStringValues(formatMessage({ id: a.titleIntlKey }), formatMessage({ id: b.titleIntlKey })),
                  )
                  .map((group, groupIndex) => (
                    <div
                      key={`column-group-${groupIndex}`}
                      style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}
                    >
                      <ModalDivider translationKey={group.titleIntlKey} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {group.columns
                          .slice()
                          .sort((columnA, columnB) => {
                            const titleA =
                              columns.find((x) => JSON.stringify(x.key) === JSON.stringify(columnA.key))?.title || "";
                            const titleB =
                              columns.find((x) => JSON.stringify(x.key) === JSON.stringify(columnB.key))?.title || "";
                            return titleA.localeCompare(titleB);
                          })
                          .map((columnDefinition) => {
                            const columnDefinitionDataIndexJson = JSON.stringify(columnDefinition.key);
                            const column = columns.find((x) => JSON.stringify(x.key) === columnDefinitionDataIndexJson);
                            if (!column?.title) {
                              return;
                            }

                            return (
                              <div
                                key={columnDefinitionDataIndexJson}
                                style={{
                                  flex: "0 0 24%",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Checkbox value={columnDefinitionDataIndexJson}>{column.title as string}</Checkbox>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
      <Tooltip placement="top" title={formatMessage({ id: "table.column.settings" })} arrow>
        <Button
          onClick={() => setOpen(true)}
          type="text"
          icon={<i className="fa-solid fa-cog"></i>}
        />
      </Tooltip>
    </>
  );
};
